import { ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { DocumentType } from "@/features/preinvest/_shared/types";
import {
  DOC_TYPE_GROUPS,
  DOC_TYPE_LABELS,
} from "@/features/preinvest/documents/labels";
import type { DossierListItem } from "@/features/preinvest/types";
import { getFolderTree } from "@/features/postinvest/documents/api";
import type { FolderNode } from "@/features/postinvest/documents/types";
import { apiGet } from "@/lib/api";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { saveAttachment, saveMessage } from "./api";
import { defaultDocType } from "./fileTypes";
import { SparkyApiError, type SparkyAttachment } from "./types";

/** What is being filed: an uploaded file or an assistant answer. */
export type SaveTarget =
  | { kind: "attachment"; attachment: SparkyAttachment }
  | { kind: "message"; messageId: string };

interface DealOption {
  id: string;
  name: string;
}

interface FolderOption {
  id: string;
  label: string;
}

function flattenFolders(
  nodes: readonly FolderNode[],
  prefix = "",
): FolderOption[] {
  return nodes.flatMap((node) => {
    const label = `${prefix}${node.name}`;
    return [
      { id: node.id, label },
      ...flattenFolders(node.children, `${label} / `),
    ];
  });
}

async function loadDeals(): Promise<DealOption[]> {
  const [active, invested] = await Promise.all([
    apiGet<DossierListItem[]>("/api/v1/preinvest/dossiers?status=active"),
    apiGet<DossierListItem[]>("/api/v1/preinvest/dossiers?status=invested"),
  ]);
  return [...active, ...invested]
    .map((item) => ({ id: item.deal_id, name: item.startup.legal_name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * "Opslaan bij deal": pick the deal, the folder (pre-invest dossier or a
 * post-invest folder) and, for files, the document type. The deal of the
 * conversation is preselected.
 */
function SaveForm({
  onOpenChange,
  target,
  defaultDealId,
  onSaved,
}: {
  onOpenChange: (open: boolean) => void;
  target: SaveTarget;
  defaultDealId: string | null;
  onSaved: (target: SaveTarget, startupName: string) => void;
}) {
  const [deals, setDeals] = useState<DealOption[]>([]);
  const [dealId, setDealId] = useState<string | null>(defaultDealId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [folders, setFolders] = useState<FolderOption[]>([]);
  const [folderId, setFolderId] = useState("");
  const attachment = target.kind === "attachment" ? target.attachment : null;
  const [docType, setDocType] = useState<DocumentType>(
    attachment ? defaultDocType(attachment) : "other",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadDeals()
      .then((list) => {
        if (!cancelled) setDeals(list);
      })
      .catch(() => {
        if (!cancelled) setError("Kon de deals niet laden.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Folders of the chosen deal; the first root folder is preselected when any exist.
  useEffect(() => {
    if (dealId === null) return;
    const chosen = dealId;
    let cancelled = false;
    getFolderTree(chosen)
      .then((tree) => {
        if (cancelled) return;
        const options = flattenFolders(tree.folders);
        setFolders(options);
        setFolderId(tree.folders[0]?.id ?? "");
      })
      .catch(() => {
        if (cancelled) return;
        setFolders([]);
        setFolderId("");
      });
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const dealName = useMemo(
    () => deals.find((d) => d.id === dealId)?.name ?? null,
    [deals, dealId],
  );

  async function submit() {
    if (dealId === null || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result =
        target.kind === "attachment"
          ? await saveAttachment(target.attachment.id, {
              deal_id: dealId,
              folder_id: folderId || null,
              doc_type: docType,
            })
          : await saveMessage(target.messageId, {
              deal_id: dealId,
              folder_id: folderId || null,
            });
      toast.success(`Opgeslagen bij ${result.startup_name}`);
      onSaved(target, result.startup_name);
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof SparkyApiError
          ? err.message
          : "Opslaan mislukt. Probeer het opnieuw.",
      );
      setSaving(false);
    }
  }

  return (
    <DialogContent className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>
          {target.kind === "message"
            ? "Antwoord opslaan bij deal"
            : "Opslaan bij deal"}
        </DialogTitle>
        <DialogDescription>
          {target.kind === "message"
            ? "Het antwoord wordt als pdf in het dossier van de deal gezet."
            : "Het bestand wordt in het dossier van de deal gezet."}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5 text-sm">
          <span className="text-muted-foreground">Deal</span>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={pickerOpen}
                aria-label="Deal"
                disabled={saving}
                className={cn(
                  "flex h-8 w-full items-center justify-between rounded-lg border border-control-border bg-card px-3 py-2 text-left text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 max-md:h-11",
                  dealName === null && "text-muted-foreground",
                )}
              >
                {dealName ?? "Kies een deal…"}
                <ChevronsUpDown
                  className="size-4 shrink-0 opacity-50"
                  aria-hidden
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-(--radix-popover-trigger-width) p-0"
              align="start"
            >
              <Command>
                <CommandInput placeholder="Zoek een deal…" />
                <CommandList>
                  <CommandEmpty>Geen deals gevonden.</CommandEmpty>
                  <CommandGroup>
                    {deals.map((deal) => (
                      <CommandItem
                        key={deal.id}
                        value={deal.name}
                        onSelect={() => {
                          setDealId(deal.id);
                          setPickerOpen(false);
                        }}
                      >
                        {deal.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-muted-foreground">Map</span>
          <NativeSelect
            aria-label="Map"
            value={folderId}
            disabled={saving}
            onChange={(event) => setFolderId(event.target.value)}
          >
            <option value="">Pre-invest dossier</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.label}
              </option>
            ))}
          </NativeSelect>
        </label>

        {attachment ? (
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted-foreground">Type document</span>
            <NativeSelect
              aria-label="Type document"
              value={docType}
              disabled={saving}
              onChange={(event) =>
                setDocType(event.target.value as DocumentType)
              }
            >
              {DOC_TYPE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.types.map((type) => (
                    <option key={type} value={type}>
                      {DOC_TYPE_LABELS[type]}
                    </option>
                  ))}
                </optgroup>
              ))}
            </NativeSelect>
          </label>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => onOpenChange(false)}
        >
          Annuleren
        </Button>
        <Button
          type="button"
          disabled={dealId === null || saving}
          onClick={() => void submit()}
        >
          Opslaan
        </Button>
      </div>
    </DialogContent>
  );
}

/** "Opslaan bij deal"; the form mounts fresh on every open, so it starts from the defaults. */
export function SaveToDealDialog({
  open,
  onOpenChange,
  target,
  defaultDealId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: SaveTarget | null;
  defaultDealId: string | null;
  onSaved: (target: SaveTarget, startupName: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && target ? (
        <SaveForm
          onOpenChange={onOpenChange}
          target={target}
          defaultDealId={defaultDealId}
          onSaved={onSaved}
        />
      ) : null}
    </Dialog>
  );
}
